import { PropsWithChildren } from 'react';
import { MdOutlineArrowRightAlt } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { toLocalDateFromApiDateKey } from '@lp/features/Scheduler/utils';
import {
  BaseResource,
  ResourceShiftTypeHrSystemConflict,
  ScheduleHrSystemConflictType,
  ShiftType,
} from '@/api/apis/resources/schedule';
import { formatDate, getFullName } from '@/utils/helpers';
import { shiftTypeNames } from '@/constants/schedule.constants';

type HrSystemConflictCardProps<R extends BaseResource, T extends ShiftType> = PropsWithChildren<{
  conflict: ResourceShiftTypeHrSystemConflict<R, T>;
  isApplyDisabled?: boolean;
  onApply: () => void;
}>;

const HrSystemLabel = () => <span className="text-base-30">(Workday)</span>;

export const HrSystemConflictCard = <R extends BaseResource, T extends ShiftType>({
  conflict,
  isApplyDisabled,
  onApply,
  children,
}: HrSystemConflictCardProps<R, T>) => {
  const { day, resource, scheduleShiftType, type } = conflict;
  // A removed conflict has no Workday side: the shift on the schedule is the one Workday set.
  const isRemoved = type === ScheduleHrSystemConflictType.HrSystemShiftTypeRemoved;

  return (
    <div className="flex bg-white rounded-[4px_8px_8px_4px] border-l-4 border-info-40 px-2 py-2 gap-2">
      <TmdxAvatar size="sm" src={resource.profilePhoto} className="mt-px" />
      <div className="flex flex-col gap-1.5 w-full">
        <div className="flex justify-between items-center gap-1">
          <span className="text-base-5 text-m-m">{getFullName(resource.firstName, resource.lastName)}</span>
          {day && (
            <span className="text-base-30 text-m-r">{formatDate(toLocalDateFromApiDateKey(day), 'MM/dd/yyyy')}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 items-center text-m-r">
          <span className="text-base-20 line-through">{shiftTypeNames[scheduleShiftType]}</span>
          {isRemoved && <HrSystemLabel />}
          <MdOutlineArrowRightAlt />
          {children}
          {!isRemoved && <HrSystemLabel />}
        </div>
        <button
          disabled={isApplyDisabled}
          className="self-end text-m-m text-info-30 hover:underline disabled:opacity-50 disabled:hover:no-underline"
          onClick={onApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};
